'use client';

import { useState, useRef, useEffect } from 'react';
import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

const notificationIcons: Record<string, string> = {
  order_new: 'ShoppingBagIcon',
  order_completed: 'CheckCircleIcon',
  order_shipped: 'TruckIcon',
  order_delivered: 'CheckCircleIcon',
  order_refunded: 'ArrowUturnLeftIcon',
  payout_confirmed: 'CurrencyDollarIcon',
  payout_failed: 'ExclamationCircleIcon',
  review_new: 'StarIcon',
  review_reply: 'ChatBubbleLeftIcon',
  support_new: 'ChatBubbleLeftRightIcon',
  support_update: 'ChatBubbleLeftRightIcon',
  support_resolved: 'CheckCircleIcon',
  ticket_reply: 'ChatBubbleLeftIcon',
  discount_alert: 'TagIcon',
  dispute_new: 'ExclamationTriangleIcon',
  dispute_response: 'ChatBubbleLeftRightIcon',
  dispute_resolved: 'CheckCircleIcon',
  system_alert: 'BellAlertIcon',
  policy_update: 'DocumentTextIcon',
  feature_announcement: 'SparklesIcon',
};

const notificationColors: Record<string, string> = {
  order_new: '#6366f1',
  order_completed: '#10b981',
  order_shipped: '#f59e0b',
  order_delivered: '#10b981',
  order_refunded: '#ef4444',
  payout_confirmed: '#10b981',
  payout_failed: '#ef4444',
  review_new: '#f59e0b',
  review_reply: '#6366f1',
  support_new: '#6366f1',
  support_update: '#f59e0b',
  support_resolved: '#10b981',
  ticket_reply: '#6366f1',
  discount_alert: '#ec4899',
  dispute_new: '#ef4444',
  dispute_response: '#f59e0b',
  dispute_resolved: '#10b981',
  system_alert: '#ef4444',
  policy_update: '#6b7280',
  feature_announcement: '#8b5cf6',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-9 h-9 rounded-lg flex items-center justify-center relative transition-all duration-200 hover:scale-110"
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          color: 'var(--text-secondary)',
        }}
        aria-label="Notifications"
      >
        <Icon name="BellIcon" size={16} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full text-white flex items-center justify-center px-1"
            style={{ background: 'var(--accent)', fontSize: '9px', fontWeight: 700 }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-11 w-80 rounded-2xl shadow-2xl z-50 overflow-hidden"
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Notifications</h3>
              {unreadCount > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-medium"
                  style={{ background: 'var(--accent)', color: 'white' }}
                >
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-medium transition-colors"
                style={{ color: 'var(--accent)' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-10">
                <Icon name="BellSlashIcon" size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => markAsRead(notif.id)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-opacity-50"
                  style={{
                    background: notif.is_read ? 'transparent' : 'var(--accent-light)',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  {/* Icon */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: `${notificationColors[notif.type] || '#6366f1'}20` }}
                  >
                    <Icon
                      name={notificationIcons[notif.type] || 'BellIcon'}
                      size={14}
                      style={{ color: notificationColors[notif.type] || '#6366f1' }}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold leading-tight" style={{ color: 'var(--text-primary)' }}>
                        {notif.title}
                      </p>
                      {!notif.is_read && (
                        <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1" style={{ background: 'var(--accent)' }} />
                      )}
                    </div>
                    <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                      {notif.message}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      {timeAgo(notif.created_at)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5" style={{ borderTop: '1px solid var(--border)' }}>
              <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                Showing last {notifications.length} notifications
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
